import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Streamdown } from 'streamdown';

import { Box, Button, Divider, Flex, Link, ListItem, OrderedList, Text, UnorderedList, useBreakpointValue } from '@bonusly/design-system';

import { splitSources } from '../lib/split-sources';
import { bizyTableComponents } from './bizy-markdown-table';

/**
 * Subset of the HAST node shape that Streamdown passes to component
 * handlers via the `node` prop. We only read tagName/properties/children
 * (and `value` on text nodes), so the loose typing here is intentional —
 * pulling in a full `hast` dependency for one prop isn't worth it.
 */
interface MarkdownNode {
	type?: string;
	value?: string;
	tagName?: string;
	properties?: { href?: string | string[] | number | boolean | null };
	children?: MarkdownNode[];
}

/**
 * Strip the `_target=self` query param a same-tab path was tagged with so
 * we can hand a clean URL to react-router.
 */
function cleanSameTabHref(href: string): string {
	return href
		.replace(/[?&]_target=self/, '')
		.replace(/\?$/, '')
		.replace(/^([^?]*?)&/, '$1?');
}

function isInternalSameTabHref(href: string | undefined): href is string {
	if (!href || !href.includes('_target=self')) return false;
	const clean = cleanSameTabHref(href);
	return clean.startsWith('/') && !clean.startsWith('//');
}

/**
 * If a paragraph node contains nothing but a single `**[Label](path?_target=self)**`
 * (i.e. a strong wrapping an internal-link anchor), return the cleaned page
 * path and the inner label nodes. Otherwise return null. We inspect the raw
 * HAST here instead of the rendered React tree so we can reliably detect the
 * pattern before the `a` handler strips `_target=self` from the href.
 */
function loneInternalPageSuggestion(node: MarkdownNode | undefined): { pagePath: string; labelNodes: MarkdownNode[] } | null {
	const onlyChild = singleSignificantChild(node?.children);
	if (onlyChild?.tagName !== 'strong') return null;
	const innerAnchor = singleSignificantChild(onlyChild.children);
	if (innerAnchor?.tagName !== 'a') return null;
	const rawHref = typeof innerAnchor.properties?.href === 'string' ? innerAnchor.properties.href : undefined;
	if (!isInternalSameTabHref(rawHref)) return null;
	return { pagePath: cleanSameTabHref(rawHref), labelNodes: innerAnchor.children ?? [] };
}

function singleSignificantChild(children: MarkdownNode[] | undefined): MarkdownNode | null {
	if (!children) return null;
	const meaningful = children.filter((c) => {
		const value = (c as { type?: string; value?: string }).value;
		const type = (c as { type?: string }).type;
		if (type === 'text') return typeof value === 'string' && value.trim().length > 0;
		return true;
	});
	return meaningful.length === 1 ? meaningful[0] : null;
}

/** Render the bold label of a page-suggestion as plain text inside a Button. */
function renderHastLabel(nodes: MarkdownNode[]): React.ReactNode {
	return nodes
		.map((n) => {
			const value = (n as { type?: string; value?: string }).value;
			const type = (n as { type?: string }).type;
			if (type === 'text') return value ?? '';
			if (n.children) return renderHastLabel(n.children);
			return '';
		})
		.join('');
}

interface BizyStreamingMessageProps {
	/** The content to render */
	content: string;
	fontSize?: 'sm' | 'md';
	/** Whether the response is complete (switches from thinking style to final style) */
	isComplete?: boolean;
	/** Called when content updates (for auto-scroll) */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running (e.g., ["Reading your notes...", "Working on recognition..."]) */
	toolProgress?: string[];
	/** Rotating thinking phrase shown when no tool progress and not yet streaming content */
	thinkingPhrase?: string;
	/** Where to render progress labels while streaming. Defaults to the legacy above-content position. */
	progressPlacement?: 'above' | 'below';
	/** Called when user clicks a suggested page link (for analytics tracking) */
	onSuggestedPageClick?: (path: string) => void;
	/** When true, suppresses the inline Sources block (continuous-chat "Today" feed removes it). */
	hideSources?: boolean;
}

/**
 * Renders Bizy content as direct markdown (no character-by-character animation).
 *
 * Used by the V2 streaming component for BOTH in-progress and completed messages
 * to avoid the jarring flash that occurs when switching between different component
 * types on completion. The same component handles both states:
 *
 * - Streaming (isComplete=false): italic text, "Thinking..." label, pulsing cursor
 * - Complete (isComplete=true): normal text, no label, no cursor
 */
export function BizyStreamingMessage({
	content,
	fontSize,
	isComplete = false,
	onContentUpdate,
	toolProgress,
	thinkingPhrase,
	progressPlacement = 'above',
	onSuggestedPageClick,
	hideSources = false,
}: BizyStreamingMessageProps) {
	const { t } = useTranslation('admin_bizy');
	const navigate = useNavigate();
	const prevContentLengthRef = useRef(content.length);

	useEffect(() => {
		if (content.length !== prevContentLengthRef.current) {
			prevContentLengthRef.current = content.length;
			onContentUpdate?.();
		}
	}, [content, onContentUpdate]);

	const textColor = isComplete ? 'text.default' : 'text.softer';
	const responsiveBodyFontSize = useBreakpointValue({ base: 'md', sm: 'sm' }) as 'md' | 'sm' | undefined;
	const bodyFontSize = fontSize ?? responsiveBodyFontSize;
	const { body, sources } = useMemo(() => splitSources(content), [content]);

	const markdownSx = useMemo(
		() => ({
			'p + p': { marginTop: '0.5em' },
			'ul, ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '4px' },
			'li + li': { marginTop: '8px' },
			'p:first-of-type': { marginTop: 0 },
			'p:last-of-type': { marginBottom: 0 },
			hr: { marginTop: '0.75em', marginBottom: '0.5em', borderColor: 'border.soft' },
		}),
		[]
	);

	const activatePageLink = useCallback(
		(path: string) => {
			onSuggestedPageClick?.(path);
			navigate(path);
		},
		[navigate, onSuggestedPageClick]
	);

	const markdownComponents = useMemo(
		() => ({
			a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
				if (isInternalSameTabHref(href)) {
					const cleanHref = cleanSameTabHref(href);
					return (
						<Link
							href={cleanHref}
							color="text.interactive"
							onClick={(e: React.MouseEvent) => {
								e.preventDefault();
								activatePageLink(cleanHref);
							}}
						>
							{children}
						</Link>
					);
				}

				return (
					<Link href={href} color="text.interactive" target="_blank" rel="noopener noreferrer">
						{children}
					</Link>
				);
			},
			p: ({ children, node }: React.HTMLAttributes<HTMLParagraphElement> & { node?: MarkdownNode }) => {
				// When a paragraph contains nothing but a single bold page-link
				// suggestion, promote it to a block-level Button. Mixed inline
				// usage stays as a normal interactive bold link so we don't
				// fragment the surrounding sentence (and leave orphan punctuation
				// dangling on its own line).
				const suggestion = loneInternalPageSuggestion(node);
				if (suggestion) {
					return (
						<Button size="sm" variant="outline" colorScheme="secondary" borderRadius={10} my={1} onClick={() => activatePageLink(suggestion.pagePath)}>
							{renderHastLabel(suggestion.labelNodes)}
						</Button>
					);
				}
				return (
					<Text fontSize={bodyFontSize} color="inherit">
						{children}
					</Text>
				);
			},
			h1: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			h2: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			h3: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			strong: ({ children }: React.HTMLAttributes<HTMLElement>) => (
				// We deliberately don't special-case bold-wrapped page links here.
				// The lone-paragraph case is promoted to a Button by the `p` handler
				// using the raw HAST tree; everything else stays inline so prose
				// surrounding the link reads naturally.
				<Text as="strong" fontSize="inherit" fontWeight="semibold" color="inherit" display="inline">
					{children}
				</Text>
			),
			ol: ({ children }: React.OlHTMLAttributes<HTMLOListElement>) => (
				<OrderedList fontSize={bodyFontSize} color="inherit">
					{children}
				</OrderedList>
			),
			ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => (
				<UnorderedList fontSize={bodyFontSize} color="inherit">
					{children}
				</UnorderedList>
			),
			li: ({ children }: React.LiHTMLAttributes<HTMLLIElement>) => <ListItem>{children}</ListItem>,
			...bizyTableComponents(bodyFontSize || 'sm'),
			pre: ({ children }: React.HTMLAttributes<HTMLPreElement>) => <pre style={{ whiteSpace: 'pre-wrap', fontSize: 'inherit' }}>{children}</pre>,
			code: ({ children }: React.HTMLAttributes<HTMLElement>) => (
				<Text as="code" fontSize={bodyFontSize} bg="bg.muted" px={1} borderRadius="sm">
					{children}
				</Text>
			),
		}),
		[bodyFontSize, activatePageLink]
	);

	const progressLabel = !isComplete ? (
		<Flex direction="column" gap={0.5} mb={progressPlacement === 'above' ? 1 : 0} mt={progressPlacement === 'below' ? 1 : 0}>
			{toolProgress && toolProgress.length > 0 ? (
				toolProgress.map((item, index) => {
					const isLatest = index === toolProgress.length - 1;
					return (
						<Text
							key={index}
							fontSize="xs"
							fontWeight="medium"
							color="text.softest"
							sx={
								isLatest
									? {
											'@keyframes pulse': {
												'0%, 100%': { opacity: 1 },
												'50%': { opacity: 0.4 },
											},
											animation: 'pulse 1.5s ease-in-out infinite',
										}
									: { opacity: 0.6 }
							}
						>
							{item}
						</Text>
					);
				})
			) : (
				<Text
					fontSize="xs"
					fontWeight="medium"
					color="text.softest"
					sx={{
						'@keyframes pulse': {
							'0%, 100%': { opacity: 1 },
							'50%': { opacity: 0.4 },
						},
						animation: 'pulse 1.5s ease-in-out infinite',
					}}
				>
					{thinkingPhrase ?? t('thinking')}
				</Text>
			)}
		</Flex>
	) : null;

	return (
		<Box alignSelf="flex-start" maxWidth="100%" pl={1} pr={3}>
			{progressPlacement === 'above' && progressLabel}

			<Box color={textColor} transition="color 0.3s ease" sx={markdownSx}>
				<Streamdown components={markdownComponents}>{body}</Streamdown>
			</Box>

			{progressPlacement === 'below' && progressLabel}

			{sources && !hideSources && (
				<Box mt={3}>
					<Divider borderColor="border.soft" />
					<Box mt={2} color="text.softer" fontStyle="normal" sx={{ 'p:first-of-type': { marginTop: 0 }, 'p:last-of-type': { marginBottom: 0 } }}>
						<Streamdown
							components={
								{
									a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
										<Link href={href} color="text.interactive" fontSize="xs" target="_blank" rel="noopener noreferrer">
											{children}
										</Link>
									),
									p: ({ children }: { children?: React.ReactNode }) => (
										<Text fontSize="xs" color="text.softer">
											{children}
										</Text>
									),
								} as Parameters<typeof Streamdown>[0]['components']
							}
						>
							{sources}
						</Streamdown>
					</Box>
				</Box>
			)}
		</Box>
	);
}
